import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export type StatusTone = "ok" | "warn" | "danger" | "info" | "neutral";

const DOT: Record<StatusTone, string> = {
  ok: "bg-(--ok)",
  warn: "bg-(--warn)",
  danger: "bg-(--danger)",
  info: "bg-(--info)",
  neutral: "bg-(--text-subtle)",
};

/** Soft semantic fill + readable on-tint text — a quiet pill that reads at a glance on a white table. */
const FILL: Record<StatusTone, string> = {
  ok: "bg-(--ok-subtle) text-(--ok)",
  warn: "bg-(--warn-subtle) text-(--warn)",
  danger: "bg-(--danger-subtle) text-(--danger)",
  info: "bg-(--info-subtle) text-(--info)",
  neutral: "bg-(--surface-sunken) text-(--text-muted) ring-1 ring-inset ring-(--border)",
};

export interface StatusPillProps {
  tone: StatusTone;
  children: ReactNode;
  className?: string;
  /** Native title attribute — an optional tooltip for a pill whose meaning needs a sentence a short
   *  label can't carry (e.g. Pool Users' "needs review" badge, r12 22/7). */
  title?: string;
}

/**
 * State indicator: a soft semantic fill, a saturated dot, and a text label. Colour is NEVER the only
 * signal — the dot AND the text both carry the meaning (colour-blind safe). Use for account status,
 * sync state, health, etc.
 */
export function StatusPill({ tone, children, className, title }: StatusPillProps) {
  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-(length:--text-xs) font-medium",
        FILL[tone],
        className,
      )}
    >
      <span className={cn("size-1.5 shrink-0 rounded-full", DOT[tone])} aria-hidden />
      {children}
    </span>
  );
}
